/**
 * AI Tools for Support
 *
 * Tools that allow Marta to interact with support requests and client logs via GraphQL.
 */

import type { ToolDefinition } from './types';
import { graphqlQuery, graphqlMutation } from '../../database/graphqlClient';

// =============================================================================
// GRAPHQL FIELD FRAGMENTS
// =============================================================================

const SUPPORT_REQUEST_FIELDS = `
  id
  userId
  title
  description
  status
  priority
  assignedTo
  createdAt
  updatedAt
`;

const CLIENT_LOG_FIELDS = `
  id
  userId
  logType
  message
  metadata
  createdAt
`;

const SUPPORT_STATS_FIELDS = `
  totalRequests
  openRequests
  resolvedRequests
  avgResolutionTime
`;

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const QUERIES = {
  supportRequests: `query ListSupportRequests { supportRequests { ${SUPPORT_REQUEST_FIELDS} } }`,
  supportRequest: `query GetSupportRequest($id: ID!) { supportRequest(id: $id) { ${SUPPORT_REQUEST_FIELDS} } }`,
  clientLogs: `query ListClientLogs { clientLogs { ${CLIENT_LOG_FIELDS} } }`,
  supportStats: `query GetSupportStats { supportStats { ${SUPPORT_STATS_FIELDS} } }`,
};

const MUTATIONS = {
  createSupportRequest: `mutation CreateSupportRequest($input: SupportRequestInput!) { createSupportRequest(input: $input) { ${SUPPORT_REQUEST_FIELDS} } }`,
  updateSupportRequestStatus: `mutation UpdateSupportRequestStatus($id: ID!, $status: String!) { updateSupportRequestStatus(id: $id, status: $status) { ${SUPPORT_REQUEST_FIELDS} } }`,
  assignSupportRequest: `mutation AssignSupportRequest($id: ID!, $assignedTo: String!) { assignSupportRequest(id: $id, assignedTo: $assignedTo) { ${SUPPORT_REQUEST_FIELDS} } }`,
  deleteSupportRequest: `mutation DeleteSupportRequest($id: ID!) { deleteSupportRequest(id: $id) }`,
  createClientLog: `mutation CreateClientLog($input: ClientLogInput!) { createClientLog(input: $input) { ${CLIENT_LOG_FIELDS} } }`,
  deleteClientLog: `mutation DeleteClientLog($id: ID!) { deleteClientLog(id: $id) }`,
};

// =============================================================================
// SUPPORT REQUEST TOOL DEFINITIONS
// =============================================================================

export const listSupportRequestsTool: ToolDefinition = {
  name: 'list_support_requests',
  description: 'Elenca le richieste di supporto. Può filtrare per stato, priorità, utente assegnato.',
  category: 'support',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.supportRequests,
  parameters: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        description: 'Filtra per stato della richiesta',
        enum: ['open', 'in_progress', 'resolved', 'closed'],
      },
      priority: {
        type: 'string',
        description: 'Filtra per priorità',
        enum: ['low', 'medium', 'high', 'urgent'],
      },
      assignedTo: {
        type: 'string',
        description: 'Filtra per utente assegnato (user ID)',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const getSupportRequestTool: ToolDefinition = {
  name: 'get_support_request',
  description: 'Ottiene i dettagli di una richiesta di supporto specifica.',
  category: 'support',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.supportRequest,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della richiesta di supporto',
      },
    },
    required: ['id'],
  },
};

export const createSupportRequestTool: ToolDefinition = {
  name: 'create_support_request',
  description: 'Crea una nuova richiesta di supporto.',
  category: 'support',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createSupportRequest,
  parameters: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Titolo della richiesta',
      },
      description: {
        type: 'string',
        description: 'Descrizione dettagliata del problema',
      },
      priority: {
        type: 'string',
        description: 'Priorità della richiesta',
        enum: ['low', 'medium', 'high', 'urgent'],
        default: 'medium',
      },
      assignedTo: {
        type: 'string',
        description: 'User ID a cui assegnare la richiesta (opzionale)',
      },
    },
    required: ['title', 'description'],
  },
};

export const updateSupportRequestStatusTool: ToolDefinition = {
  name: 'update_support_request_status',
  description: 'Aggiorna lo stato di una richiesta di supporto.',
  category: 'support',
  operation: 'update',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.updateSupportRequestStatus,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della richiesta di supporto',
      },
      status: {
        type: 'string',
        description: 'Nuovo stato',
        enum: ['open', 'in_progress', 'resolved', 'closed'],
      },
    },
    required: ['id', 'status'],
  },
};

export const assignSupportRequestTool: ToolDefinition = {
  name: 'assign_support_request',
  description: 'Assegna una richiesta di supporto a un utente.',
  category: 'support',
  operation: 'update',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.assignSupportRequest,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della richiesta di supporto',
      },
      assignedTo: {
        type: 'string',
        description: 'User ID a cui assegnare',
      },
    },
    required: ['id', 'assignedTo'],
  },
};

export const deleteSupportRequestTool: ToolDefinition = {
  name: 'delete_support_request',
  description: 'Elimina una richiesta di supporto. ATTENZIONE: Questa azione è irreversibile.',
  category: 'support',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteSupportRequest,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della richiesta di supporto da eliminare',
      },
    },
    required: ['id'],
  },
};

// =============================================================================
// CLIENT LOG TOOL DEFINITIONS
// =============================================================================

export const listClientLogsTool: ToolDefinition = {
  name: 'list_client_logs',
  description: 'Elenca i log del client. Può filtrare per tipo di log.',
  category: 'support',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.clientLogs,
  parameters: {
    type: 'object',
    properties: {
      logType: {
        type: 'string',
        description: 'Filtra per tipo di log',
        enum: ['error', 'warning', 'info', 'debug'],
      },
      userId: {
        type: 'string',
        description: 'Filtra per user ID',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 50)',
      },
    },
  },
};

export const createClientLogTool: ToolDefinition = {
  name: 'create_client_log',
  description: 'Crea un nuovo log del client (per debug/diagnostica).',
  category: 'support',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createClientLog,
  parameters: {
    type: 'object',
    properties: {
      logType: {
        type: 'string',
        description: 'Tipo di log',
        enum: ['error', 'warning', 'info', 'debug'],
      },
      message: {
        type: 'string',
        description: 'Messaggio del log',
      },
      metadata: {
        type: 'string',
        description: 'Metadata aggiuntivi in formato JSON string',
      },
    },
    required: ['logType', 'message'],
  },
};

export const deleteClientLogTool: ToolDefinition = {
  name: 'delete_client_log',
  description: 'Elimina un log del client.',
  category: 'support',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteClientLog,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del log da eliminare',
      },
    },
    required: ['id'],
  },
};

// =============================================================================
// SUPPORT STATS TOOL DEFINITION
// =============================================================================

export const getSupportStatsTool: ToolDefinition = {
  name: 'get_support_stats',
  description: 'Ottiene statistiche sulle richieste di supporto (totali, aperte, risolte, tempo medio).',
  category: 'support',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.supportStats,
  parameters: {
    type: 'object',
    properties: {},
  },
};

// =============================================================================
// TOOL EXECUTION FUNCTIONS
// =============================================================================

interface SupportRequest {
  id: string;
  userId?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  assignedTo?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

interface ClientLog {
  id: string;
  userId?: string;
  logType?: string;
  message?: string;
  metadata?: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface SupportStats {
  totalRequests?: number;
  openRequests?: number;
  resolvedRequests?: number;
  avgResolutionTime?: number;
  [key: string]: unknown;
}

export async function executeListSupportRequests(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ supportRequests: SupportRequest[] }>(QUERIES.supportRequests);
  let requests = result.supportRequests || [];

  // Apply status filter
  if (params.status) {
    requests = requests.filter(r => r.status === params.status);
  }

  // Apply priority filter
  if (params.priority) {
    requests = requests.filter(r => r.priority === params.priority);
  }

  // Apply assignedTo filter
  if (params.assignedTo) {
    requests = requests.filter(r => r.assignedTo === params.assignedTo);
  }

  // Sort by createdAt descending (newest first)
  requests.sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  requests = requests.slice(0, limit);

  return { requests, count: requests.length };
}

export async function executeGetSupportRequest(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ supportRequest: SupportRequest }>(QUERIES.supportRequest, { id: params.id });

  if (!result.supportRequest) {
    return { error: 'Richiesta di supporto non trovata', found: false };
  }

  return { request: result.supportRequest, found: true };
}

export async function executeCreateSupportRequest(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    title: params.title,
    description: params.description,
    priority: params.priority || 'medium',
    assignedTo: params.assignedTo || null,
  };

  const result = await graphqlMutation<{ createSupportRequest: SupportRequest }>(
    MUTATIONS.createSupportRequest,
    { input }
  );

  return result.createSupportRequest;
}

export async function executeUpdateSupportRequestStatus(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlMutation<{ updateSupportRequestStatus: SupportRequest }>(
    MUTATIONS.updateSupportRequestStatus,
    { id: params.id, status: params.status }
  );

  return result.updateSupportRequestStatus;
}

export async function executeAssignSupportRequest(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlMutation<{ assignSupportRequest: SupportRequest }>(
    MUTATIONS.assignSupportRequest,
    { id: params.id, assignedTo: params.assignedTo }
  );

  return result.assignSupportRequest;
}

export async function executeDeleteSupportRequest(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteSupportRequest, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeListClientLogs(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ clientLogs: ClientLog[] }>(QUERIES.clientLogs);
  let logs = result.clientLogs || [];

  // Apply logType filter
  if (params.logType) {
    logs = logs.filter(l => l.logType === params.logType);
  }

  // Apply userId filter
  if (params.userId) {
    logs = logs.filter(l => l.userId === params.userId);
  }

  // Sort by createdAt descending (newest first)
  logs.sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 50;
  logs = logs.slice(0, limit);

  return { logs, count: logs.length };
}

export async function executeCreateClientLog(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    logType: params.logType,
    message: params.message,
    metadata: params.metadata || null,
  };

  const result = await graphqlMutation<{ createClientLog: ClientLog }>(
    MUTATIONS.createClientLog,
    { input }
  );

  return result.createClientLog;
}

export async function executeDeleteClientLog(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteClientLog, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeGetSupportStats(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ supportStats: SupportStats }>(QUERIES.supportStats);

  return result.supportStats || {
    totalRequests: 0,
    openRequests: 0,
    resolvedRequests: 0,
    avgResolutionTime: 0,
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export const supportTools: ToolDefinition[] = [
  listSupportRequestsTool,
  getSupportRequestTool,
  createSupportRequestTool,
  updateSupportRequestStatusTool,
  assignSupportRequestTool,
  deleteSupportRequestTool,
  listClientLogsTool,
  createClientLogTool,
  deleteClientLogTool,
  getSupportStatsTool,
];

export const supportToolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  list_support_requests: executeListSupportRequests,
  get_support_request: executeGetSupportRequest,
  create_support_request: executeCreateSupportRequest,
  update_support_request_status: executeUpdateSupportRequestStatus,
  assign_support_request: executeAssignSupportRequest,
  delete_support_request: executeDeleteSupportRequest,
  list_client_logs: executeListClientLogs,
  create_client_log: executeCreateClientLog,
  delete_client_log: executeDeleteClientLog,
  get_support_stats: executeGetSupportStats,
};
